import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Badge from '../components/Badge';

const BadgeIntegrationPage: React.FC = () => {
  const [selectedBadgeType, setSelectedBadgeType] = useState<'perfect' | 'verified' | 'organic' | 'sustainable'>('perfect');
  const [badgeCode, setBadgeCode] = useState('DEMO123456');
  const [copied, setCopied] = useState<'js' | 'html' | 'url' | null>(null);

  // Example badge for preview
  const exampleBadge = {
    type: selectedBadgeType,
    code: badgeCode,
    issueDate: new Date().toISOString()
  };

  // Base URL for hosted badge images
  const baseImageUrl = 'https://goodcart.org/badges';

  // Generate badge image URL
  const getBadgeImageUrl = () => {
    return `${baseImageUrl}/${selectedBadgeType}/${badgeCode}.svg`;
  };

  // Generate HTML code for embedding
  const getHtmlCode = () => {
    return `<a href="https://goodcart.org/badge-validation?code=${badgeCode}" 
  title="GoodCart ${selectedBadgeType === 'perfect' ? 'Perfect 100%' : selectedBadgeType.charAt(0).toUpperCase() + selectedBadgeType.slice(1)} Badge" 
  target="_blank" rel="noopener">
  <img src="${getBadgeImageUrl()}" alt="GoodCart ${selectedBadgeType === 'perfect' ? 'Perfect 100%' : selectedBadgeType.charAt(0).toUpperCase() + selectedBadgeType.slice(1)} Badge" width="150" height="50" />
</a>`;
  };

  // Generate JavaScript snippet
  const getJsCode = () => {
    return `<script>
  (function() {
    var gc = document.createElement('script');
    gc.type = 'text/javascript';
    gc.async = true;
    gc.src = 'https://goodcart.org/js/badge-validator.js';
    gc.setAttribute('data-badge-code', '${badgeCode}');
    gc.setAttribute('data-badge-type', '${selectedBadgeType}');
    var s = document.getElementsByTagName('script')[0];
    s.parentNode.insertBefore(gc, s);
  })();
</script>
<div id="goodcart-badge-${badgeCode}"></div>`;
  };

  // Copy to clipboard function
  const copyToClipboard = (text: string, type: 'js' | 'html' | 'url') => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Badge Integration Options</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Showcase your GoodCart badges on your website or marketing materials with these integration options.
            </p>
          </motion.div>
          
          {/* Badge Configuration */}
          <motion.div 
            className="bg-white rounded-lg shadow-md p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Configure Your Badge</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="mb-4">
                  <label htmlFor="badgeType" className="block text-sm font-medium text-gray-700 mb-1">
                    Badge Type
                  </label>
                  <select
                    id="badgeType"
                    value={selectedBadgeType}
                    onChange={(e) => setSelectedBadgeType(e.target.value as any)}
                    className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                  >
                    <option value="perfect">GoodCart Perfect 100%</option>
                    <option value="verified">Verified</option>
                    <option value="organic">Organic</option>
                    <option value="sustainable">Sustainable</option>
                  </select>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="badgeCode" className="block text-sm font-medium text-gray-700 mb-1">
                    Badge Code
                  </label>
                  <input
                    type="text"
                    id="badgeCode"
                    value={badgeCode}
                    onChange={(e) => setBadgeCode(e.target.value)}
                    className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                    placeholder="Enter your badge code"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    This should be the unique code provided when your badge was issued.
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg flex flex-col items-center justify-center">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Badge Preview</h3>
                <div className="mb-4">
                  <Badge badge={exampleBadge} size="lg" showExplanation={true} />
                </div>
                <p className="text-sm text-gray-600 text-center">
                  This is how your badge will appear when integrated.
                </p>
              </div>
            </div>
          </motion.div>
          
          {/* Integration Options */}
          <motion.div 
            className="bg-white rounded-lg shadow-md p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Integration Options</h2>
            
            {/* HTML Embed Code */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">HTML Embed Code</h3>
              <p className="text-gray-600 text-sm mb-4">
                Copy and paste this HTML code to embed the badge on your website.
              </p>
              
              <div className="relative">
                <pre className="bg-gray-50 p-4 rounded-md text-sm overflow-x-auto">{getHtmlCode()}</pre>
                <button
                  onClick={() => copyToClipboard(getHtmlCode(), 'html')}
                  className="absolute top-2 right-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded-md text-xs font-medium transition-colors"
                >
                  {copied === 'html' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            
            {/* JavaScript Snippet */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">JavaScript Snippet</h3>
              <p className="text-gray-600 text-sm mb-4">
                Use this JavaScript snippet for dynamic badge validation that updates automatically.
              </p>
              
              <div className="relative">
                <pre className="bg-gray-50 p-4 rounded-md text-sm overflow-x-auto">{getJsCode()}</pre>
                <button
                  onClick={() => copyToClipboard(getJsCode(), 'js')}
                  className="absolute top-2 right-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded-md text-xs font-medium transition-colors"
                >
                  {copied === 'js' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            
            {/* Image URL */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Hosted Image URL</h3>
              <p className="text-gray-600 text-sm mb-4">
                Direct link to the badge image for use in marketing materials or custom implementations.
              </p>
              
              <div className="relative">
                <div className="bg-gray-50 p-4 rounded-md text-sm overflow-x-auto break-all">
                  {getBadgeImageUrl()}
                </div>
                <button
                  onClick={() => copyToClipboard(getBadgeImageUrl(), 'url')}
                  className="absolute top-2 right-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded-md text-xs font-medium transition-colors"
                >
                  {copied === 'url' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </motion.div>
          
          {/* Verification Note */}
          <motion.div 
            className="mt-8 bg-brand-green-50 border border-brand-green-200 rounded-lg p-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-brand-green-600 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-brand-green-800 font-medium mb-1">Verification Information</h3>
                <p className="text-brand-green-700 text-sm">
                  All badge integrations include verification links that allow customers to verify the authenticity of your badge. 
                  The JavaScript snippet also provides real-time validation to ensure the badge is current and hasn't been revoked.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BadgeIntegrationPage;